<template>
  <div class="relative w-full">
    <Field v-if="!hideLabel" class="gap-1">
      <FieldLabel>{{ label }}</FieldLabel>
      <FieldDescription v-if="helpText">{{ helpText }}</FieldDescription>
    </Field>

    <Popover v-model:open="isOpen">
      <PopoverAnchor as-child>
        <div class="relative">
          <Input
            v-model="query"
            placeholder="Search..."
            @focus="handleFocus"
            @blur="handleBlur"
            @input="justSelected = false"
          />
          <button
            v-if="query"
            type="button"
            class="absolute right-2 top-0 bottom-0 flex items-center text-muted-foreground hover:text-foreground"
            @mousedown.prevent="clearInput"
          >
            <vue-feather type="x" size="16" />
          </button>
        </div>
      </PopoverAnchor>

      <PopoverContent
        class="p-0 z-[9999]"
        :style="{ width: `${inputWidth}px` }"
        :side-offset="4"
        @open-auto-focus.prevent
      >
        <div class="max-h-72 overflow-y-auto">
          <div v-if="showLoader" class="flex items-center justify-center p-3">
            <div class="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent opacity-50" />
          </div>
          <div v-else-if="noResults" class="p-3 text-center text-sm text-muted-foreground italic">No results found</div>
          <button
            v-for="item in suggestions"
            :key="item.place_id"
            class="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent cursor-pointer text-left"
            @mousedown.prevent="selectPlace(item)"
          >
            <vue-feather type="map-pin" size="16" class="shrink-0 text-muted-foreground" />
            <span>{{ item.description }}</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>

    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { usePlacesAutocomplete } from 'vue-use-places-autocomplete';
import { PopoverAnchor } from 'reka-ui';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { Field, FieldLabel, FieldDescription } from '@/components/ui/field';

type PlaceSuggestion = {
  place_id: string;
  description: string;
};

type Props = {
  modelValue?: string | string[];
  label?: string;
  helpText?: string;
  hideLabel?: boolean;
  autocompleteType?: 'address' | '(cities)';
};

const emit = defineEmits<{
  'update:modelValue': [value: string];
  placeSelected: [item: PlaceSuggestion];
}>();

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: 'Location',
  helpText: undefined,
  hideLabel: false,
  autocompleteType: 'address',
});

const query = ref('');
const isFocused = ref(false);
const inputWidth = ref(0);
const hasSearched = ref(false);

const { suggestions, loading } = usePlacesAutocomplete(query, {
  debounce: 300,
  minLengthAutocomplete: 2,
  autocompletionRequest: {
    types: [props.autocompleteType],
    componentRestrictions: { country: 'us' },
  },
});

watch(loading, isLoading => {
  if (isLoading) hasSearched.value = true;
});

watch(query, val => {
  hasSearched.value = false;
  emit('update:modelValue', val);
});

const showLoader = computed(() => loading.value || (!hasSearched.value && suggestions.value.length === 0));
const noResults = computed(() => hasSearched.value && !loading.value && suggestions.value.length === 0);
const isOpen = computed(() => isFocused.value && query.value.length >= 2 && !justSelected.value);

const handleFocus = (e: FocusEvent) => {
  isFocused.value = true;
  const target = e.target as HTMLElement;
  inputWidth.value = target.parentElement?.offsetWidth || target.offsetWidth;
};
const handleBlur = () => setTimeout(() => (isFocused.value = false), 150);
const clearInput = () => (query.value = '');

const justSelected = ref(false);

const selectPlace = (item: PlaceSuggestion) => {
  justSelected.value = true;
  query.value = '';
  emit('placeSelected', item);
};

watch(
  () => props.modelValue,
  newVal => (query.value = typeof newVal === 'string' ? newVal : ''),
  { immediate: true }
);
</script>
